/**
 * Size Guide CRUD Policy (TV1-116 #14)
 *
 * Drains the privileged `loading.pendingCrud` queue (armed by the user
 * `crud/upsert` / `crud/delete` commands), issuing the backend `UpsertSizeGuide`
 * / `DeleteSizeGuide` writes, and dispatches `crud/done` per processed op so the
 * queue advances. Mirrors `inventory/priceList`'s `priceListCrudPolicy` 1:1:
 * - binding named `backend` so `no-ungated-command` detects the privileged
 *   writes; the method NAME is resolved from the `commandOperations` map below;
 * - single-flight `draining` guard; FIFO drain re-reading state each iteration
 *   so ops appended mid-drain are picked up;
 * - per-op failures are collected (logged + carried on `crud/done.error`) and
 *   STILL pop the queue, so one failure never wedges the drain.
 *
 * Command-body mapping (FE canonical guide → `UpsertSizeGuide` body, which is
 * `SizeGuideCreatedEventSchema`: `{ name, measureType, columns, rows,
 * guideAttributes? }`): the `SizeGuideReadmodelType` on the op carries every
 * field 1:1 — `name`, `measureType`, `columns`, `rows`, `guideAttributes` — and
 * the FE `SizeGuideRow` (`{ id, cells: Record<string,string> }`) matches the
 * schema row shape. `bindings` / `remoteChartId` are deliberately NOT sent: a
 * user upsert can never fabricate a synced binding (aggregate INV-5). The
 * `isImport` flag does not change the body — it only distinguishes the trigger
 * source on the user side.
 *
 * TODO(platform-validation generate-all): the `UpsertSizeGuide` /
 * `DeleteSizeGuide` operations are authored in `teselly-domain`
 * (`items-publishing/sizeGuide-integration.types.ts`) but the SizeGuide
 * aggregate + client operations have NOT been generated into
 * `@bett3r-dev/teselly-client-library` yet (they are absent from
 * `serverClientOperations`, so `'UpsertSizeGuide'` / `'DeleteSizeGuide'` are not
 * yet assignable to `ServerClientCommandName`). Until `yarn generate-all` lands
 * those operations, the `commandOperations` map below and the `backend.commands`
 * call will not typecheck — this is the intended "real signal" the ticket calls
 * out, NOT a defect to paper over with `as any`. Once generated, this file
 * compiles and runs with zero further edits.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import {
  CommandOperationMap,
  resolveCommandName
} from '@commandDrivenStateEngine/core/invariants';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import {
  SizeGuideReadmodelType,
  SizeGuidesState
} from '../state';
import { SizeGuidesCommand } from '../commands';

/**
 * `UpsertSizeGuide` body — the `SizeGuideCreatedEventSchema` slice. Kept local
 * (matching priceList's local `PriceListPayload`) until the teselly-domain
 * `UpsertSizeGuideCommandType` is re-exported through a stable barrel for the FE.
 */
type UpsertSizeGuidePayload = {
  name: string;
  measureType: SizeGuideReadmodelType['measureType'];
  columns: SizeGuideReadmodelType['columns'];
  rows: SizeGuideReadmodelType['rows'];
  guideAttributes: SizeGuideReadmodelType['guideAttributes'];
  importedFrom?: SizeGuideReadmodelType['importedFrom'];
};

/**
 * Command → backend operation map. Anchors the policy's method selection on the
 * same operation that owns the capability (parity with priceList's
 * `commandOperations`). `resolveCommandName` reads the entry for the probed
 * command type and yields the `ServerClientCommandName` to invoke.
 *
 * NOTE: `'UpsertSizeGuide'` / `'DeleteSizeGuide'` are not yet members of
 * `ServerClientCommandName` (pending generate-all — see the file header TODO),
 * so this literal map is the surface that will report the missing operations.
 */
const commandOperations: CommandOperationMap<SizeGuidesState, SizeGuidesCommand> = {
  'sizeGuides/crud/upsert': 'UpsertSizeGuide',
  'sizeGuides/crud/delete': 'DeleteSizeGuide'
};

const upsertProbe: SizeGuidesCommand = {
  type: 'sizeGuides/crud/upsert',
  payload: { id: '', guide: {} as SizeGuideReadmodelType }
};
const deleteProbe: SizeGuidesCommand = {
  type: 'sizeGuides/crud/delete',
  payload: { id: '' }
};

/**
 * Map the canonical guide to the `UpsertSizeGuide` body. Every field is sent
 * verbatim from the FE readmodel shape; `bindings` / `remoteChartId` are
 * intentionally omitted (INV-5 — a user write can never fabricate a synced
 * binding). `importedFrom` IS forwarded (when present) so a channel import keeps
 * its provenance — it's a lightweight marker, not a binding, so INV-5 leaves it
 * untouched.
 */
const parse = ( guide: SizeGuideReadmodelType ): UpsertSizeGuidePayload => ({
  name: guide.name,
  measureType: guide.measureType,
  columns: guide.columns,
  rows: guide.rows,
  guideAttributes: guide.guideAttributes,
  ...( guide.importedFrom ? { importedFrom: guide.importedFrom } : {})
});

export const createSizeGuideCrudPolicy = (
  backend: ClientLibraryType
): Policy<SizeGuidesState, SizeGuidesCommand> => {
  let draining = false;

  return {
    name: 'size-guide-crud',

    shouldReact: ( prev, next ) =>
      prev.loading.pendingCrud.length === 0 && next.loading.pendingCrud.length > 0,

    onStateChange: async ({ ctx }) => {
      const { getState } = ctx;
      if ( draining ) return;
      draining = true;
      try {
        // Drain FIFO; ops appended while draining are picked up because we
        // re-read state each iteration.
        while ( true ) {
          const op = getState().loading.pendingCrud[0];
          if ( !op ) break;
          try {
            if ( op.kind === 'upsert' ) {
              const name = resolveCommandName( commandOperations, upsertProbe, getState());
              if ( name === undefined ) throw new Error( 'crud/upsert unmapped in commandOperations' );
              await backend.commands[ name ]({ id: op.id, body: parse( op.guide ) });
            } else {
              const name = resolveCommandName( commandOperations, deleteProbe, getState());
              if ( name === undefined ) throw new Error( 'crud/delete unmapped in commandOperations' );
              await backend.commands[ name ]({ id: op.id });
            }
            ctx.dispatch(
              { type: 'sizeGuides/crud/done', payload: { id: op.id }},
              { source: 'api' }
            );
          } catch ( error ) {
            console.error( '[sizeGuides] CRUD op failed:', op, error );
            ctx.dispatch(
              {
                type: 'sizeGuides/crud/done',
                payload: { id: op.id, error: error instanceof Error ? error.message : 'Unknown error' }
              },
              { source: 'api' }
            );
          }
        }
      } finally {
        draining = false;
      }
    }
  };
};
